import { useCallback, useEffect, useRef, useState } from 'react';

import { MS_IN_S } from '../../../constants';
import { useContentRequirementsContext } from '../../../features/RestrictNavigation/ContentRequirementsProvider';
import { Guid } from '../../../store/model/guid';
import { useStudentSelector } from '../../../store/selectors/useStudentSelector';

const DELAY_FOR_ASSET_LOAD_MS = 500;

function computeDelay(
  displayDelayInSeconds: number,
  parentDisplayDelayInSeconds: number | undefined,
  time: number,
  audioDuration: number
) {
  const actualDelay = parentDisplayDelayInSeconds
    ? displayDelayInSeconds - parentDisplayDelayInSeconds
    : displayDelayInSeconds;

  if (time > 0) {
    return Math.min(actualDelay, time);
  }
  if (audioDuration > 0) {
    return Math.min(actualDelay, audioDuration);
  }
  return actualDelay;
}

export const useContentWithDelay = (id: Guid, displayDelayInSeconds: number, parentDisplayDelayInSeconds?: number) => {
  const { isRestricted, isRestrictedByListenTime, fulfilled, time } = useContentRequirementsContext();
  const audioData = useStudentSelector((studentStore) => studentStore.audioState.get(id));
  // To calculate the real delay, we first check if the content has a parent delay (e.g. widget in section)
  // and then calculate the difference.
  // The delayed content also cannot be longer than the time restriction on the parent item, so if it is,
  // we return the restriction time of the item as a delay instead.
  const delayInSeconds = computeDelay(
    displayDelayInSeconds,
    parentDisplayDelayInSeconds,
    time,
    audioData?.duration ?? 0
  );

  const ref = useRef<HTMLDivElement | null>(null);

  const defaultIsVisible = delayInSeconds <= 0 || fulfilled;
  const [isVisible, setIsVisible] = useState<boolean>(defaultIsVisible);

  const shouldMakeVisible = delayInSeconds <= (audioData?.currentTime ?? 0);

  const scrollDelayedElementIntoView = useCallback(() => {
    const timer = setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }, DELAY_FOR_ASSET_LOAD_MS);
    return () => clearTimeout(timer);
  }, [ref]);

  useEffect(() => {
    if (isVisible) {
      return;
    }
    if (fulfilled || !isRestricted) {
      setIsVisible(true);
      return;
    }
    if (isRestrictedByListenTime) {
      if (shouldMakeVisible) {
        setIsVisible(true);
        scrollDelayedElementIntoView();
      }
    } else {
      const timeOut = setTimeout(() => {
        setIsVisible(true);
        scrollDelayedElementIntoView();
      }, delayInSeconds * MS_IN_S);
      return () => clearTimeout(timeOut);
    }
    return;
  }, [
    delayInSeconds,
    fulfilled,
    isRestricted,
    isRestrictedByListenTime,
    isVisible,
    scrollDelayedElementIntoView,
    shouldMakeVisible,
  ]);

  return { isVisible, ref };
};
